// Что значит дата «По» у назначения: фраза под полем, одна на все места, где её ставят.
//
// ## Зачем фраза, а не только подпись поля
//
// 10.10.2026 финансовый директор поставил людям «По 30.09», понимая это как «период работы», то
// есть «работает в сентябре». Для программы «По» — последний день назначения ВКЛЮЧИТЕЛЬНО: с
// первого октября человека нет ни в табеле, ни в ведомости. Замер боя 19.10.2026: тридцать человек
// «на вахте» выпали из октября именно так. Подпись «По» правды не говорит, поэтому под полем стоит
// последствие с датой: «С 01.10.2026 человека не будет в табеле и ведомости этого поста».
//
// ## Почему в общем слое
//
// Дату «По» ставят в двух разделах: в «Людях» (постановка, укомплектование, продление из карточки)
// и в «Расчёте» (продление пачкой поверх табеля). Разделы друг к другу за кодом не ходят, а два
// текста одного последствия разошлись бы на первой же правке.

import { formatDateOnly, formatDayAfter } from '@/shared/time/format';
import { formatPeriodKey, shiftPeriod, type Period } from '@/shared/time/period';

/**
 * В поле «По» что-то набрано, а целой даты нет: недописана («31.10.20») или такой не бывает
 * («31.02.2026»). Поле даты отдаёт наружу пустую строку в обоих случаях, а пустое «По» значит
 * «без даты окончания», то есть недописанная дата молча превращалась в бессрочное назначение.
 * Ровно на этом поле заказчик ошибся 10.10.2026, поэтому такое нажатие не уходит на сервер.
 *
 * `набрано` — что стоит в поле на экране (его ловит обёртка поля по событию ввода).
 */
export function датаПоНедописана(датаПо: string, набрано: string): boolean {
  return датаПо === '' && набрано.replace(/\D/g, '') !== '';
}

/** Что сказать, когда дата «По» недописана. Одна фраза на все места, где есть поле «По». */
export const ДАТА_ПО_НЕДОПИСАНА =
  'Дата набрана не целиком или такой даты нет. Допишите её как 30.09.2026 или очистите поле: пустое значит «без даты окончания».';

// ── Давно кончившееся назначение ─────────────────────────────────────────────────────────
//
// Продление не заводит второго назначения, а удлиняет прежнее: кончившийся 31.07 вернётся во ВСЕ
// табели с 01.08, то есть и в августовский, и в сентябрьский. Для ошибки 10.10.2026 («По 30.09»
// как «период работы») это ровно то, что нужно. А человек, который правда уезжал на месяц и
// вернулся, получил бы пустые строки за месяцы, когда его не было, — ему нужна новая постановка с
// нужного дня. Поэтому давнее назначение заранее не отмечается и называется словами.

/**
 * Назначение кончилось раньше ПРОШЛОГО месяца табеля: для октябрьского табеля — раньше 01.09.
 * Кончившиеся в сентябре и октябре — почти всегда та самая ошибка, их панель отмечает сама.
 */
export function кончилосьДоПрошлогоМесяца(датаКонца: string, месяц: Period): boolean {
  return датаКонца < `${formatPeriodKey(shiftPeriod(месяц, -1))}-01`;
}

/**
 * Между последним днём назначения и сегодняшним больше месяца: «31.08» при «19.10». Месяц
 * календарный: тот же день месяцем раньше, а если такого дня нет — последний день того месяца.
 * Там, где табеля месяца под рукой нет (карточка человека, «Дела»), считается так.
 */
export function разрывБольшеМесяца(датаКонца: string, сегодня: string): boolean {
  const части = /^(\d{4})-(\d{2})-(\d{2})$/.exec(сегодня);
  if (части === null) {
    return false;
  }
  const прошлый = shiftPeriod({ year: Number(части[1]), month: Number(части[2]) }, -1);
  const днейВПрошлом = new Date(Date.UTC(прошлый.year, прошлый.month, 0)).getUTCDate();
  const день = Math.min(Number(части[3]), днейВПрошлом);
  return датаКонца < `${formatPeriodKey(прошлый)}-${String(день).padStart(2, '0')}`;
}

/** Что сказать о давно кончившемся назначении. Одна фраза на карточку и на панель табеля. */
export function давноКончилось(датаКонца: string): string {
  return `Назначение кончилось ${formatDateOnly(датаКонца)}: продление вернёт человека во все табели с ${formatDayAfter(датаКонца)}. Если он был в отъезде, поставьте его на пост заново с нужного дня.`;
}

/**
 * Уже созданная ведомость продлённого сама не возьмёт: её строки собраны при создании. Табель
 * строится из назначений и подхватывает продление сразу, ведомость — нет. Фраза осторожная («если
 * создана без него»): у кончившегося 15.10 октябрьская ведомость его уже содержит.
 */
export function вСозданнуюВедомость(кого: 'человек' | 'люди'): string {
  return кого === 'человек'
    ? 'Если ведомость месяца уже создана без него, откройте её и нажмите «Добавить человека»: сама она его не возьмёт.'
    : 'Если ведомость месяца уже создана без них, откройте её и нажмите «Добавить человека»: сама она их не возьмёт.';
}

/**
 * Кого касается дата: одного человека на его посту, нескольких на ОДНОМ посту (укомплектование)
 * или нескольких на РАЗНЫХ постах (продление пачкой).
 */
export type КогоКасаетсяДата = 'человек' | 'люди поста' | 'люди постов';

/**
 * Последствие даты «По» словами. `null` — поле пусто: назначение будет без даты окончания, и
 * говорить о последнем дне нечего (место вызова оставляет свой текст про пустое поле).
 */
export function последствиеДатыПо(
  датаПо: string,
  кого: КогоКасаетсяДата = 'человек',
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(датаПо)) {
    return null;
  }
  const день = formatDayAfter(датаПо);
  if (кого === 'люди поста') {
    return `С ${день} этих людей не будет в табеле и ведомости этого поста.`;
  }
  if (кого === 'люди постов') {
    return `С ${день} этих людей не будет в табеле и ведомостях их постов.`;
  }
  return `С ${день} человека не будет в табеле и ведомости этого поста.`;
}
